<template>
  <div data-testid="installationPointTable">
    <div v-if="failedInstallationPoints.length > 0">
      <div class="mb-3">
        <BaseChip
          :icon="NOT_MOUNTED_ICON"
          :number="failedInstallationPoints.length"
          border-color="#ea2020"
          custom-color="#a20101"
        />
        <span class="ml-1 lg:text-lg">Keine Montage möglich</span>
      </div>

      <table
        class="m-0! w-full rounded-xl text-left dark:bg-dark-3"
        aria-label="Failed installation devices"
        data-testid="failed_attempt_devices_table"
      >
        <thead class="bg-light-0 text-xs uppercase dark:bg-dark-2">
          <tr>
            <th scope="col" class="rounded-tl-xl py-3 pl-6">Gerätetyp</th>
            <th scope="col" class="py-3">Grund</th>
            <th scope="col" class="rounded-tr-xl px-6 py-3"></th>
          </tr>
        </thead>
        <tbody>
          <InstallationPointTableElement
            v-for="(ip, index) in failedInstallationPoints"
            :key="ip.id"
            :installation-point="ip"
            :is-last="index === failedInstallationPoints.length - 1"
            :on-edit="onEdit"
            :on-delete="onDelete"
            :on-exchange="onExchange"
          />
        </tbody>
      </table>
    </div>

    <div v-if="unmountedInstallationPoints.length > 0">
      <div class="mb-3">
        <BaseChip
          :number="unmountedInstallationPoints.length"
          border-color="#ffa451"
          custom-color="#744215"
          text="demontiert"
        />
        <span class="ml-1 lg:text-lg">Demontierte Geräte</span>
      </div>

      <table
        class="m-0! w-full rounded-xl text-left dark:bg-dark-3"
        aria-label="Unmounted devices"
        data-testid="unmounted_devices_table"
      >
        <thead class="bg-light-0 text-xs uppercase dark:bg-dark-2">
          <tr>
            <th scope="col" class="rounded-tl-xl py-3 pl-6">Gerätetyp</th>
            <th scope="col" class="py-3">Grund</th>
            <th scope="col" class="rounded-tr-xl py-3 pl-6"></th>
          </tr>
        </thead>
        <tbody>
          <InstallationPointTableElement
            v-for="(ip, index) in unmountedInstallationPoints"
            :key="ip.id"
            :installation-point="ip"
            :is-last="index === unmountedInstallationPoints.length - 1"
            :on-edit="onEdit"
            :on-delete="onDelete"
            :on-exchange="onExchange"
            :on-tkd-service="onTkdService"
          />
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import InstallationPointTableElement from '@/components/Forms/DeviceListFormComponents/InstallationPointTableElement.vue';
import BaseChip from '@/components/UI/Chip/BaseChip.vue';
import { NOT_MOUNTED_ICON } from '@/enums/icons';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';

const InstallationPointTable = {
  FAILED_INSTALLATION: 'FAILED_INSTALLATION',
  UNMOUNTED: 'UNMOUNTED',
} as const;

type InstallationPointTableType =
  (typeof InstallationPointTable)[keyof typeof InstallationPointTable];

const props = defineProps<{
  type: InstallationPointTableType;
  installationPoints: InstallationPoint[];
  onEdit: (installationPoint: InstallationPoint) => void;
  onDelete?: (installationPoint: InstallationPoint) => void;
  onExchange: (installationPoint: InstallationPoint) => void;
  onTkdService?: (installationPoint: InstallationPoint) => void;
}>();

const sortedInstallationPoints = computed(() =>
  [...props.installationPoints].sort((a, b) => a.ordinal - b.ordinal)
);

const failedInstallationPoints = computed(() =>
  props.type === InstallationPointTable.FAILED_INSTALLATION
    ? sortedInstallationPoints.value
    : []
);

const unmountedInstallationPoints = computed(() =>
  props.type === InstallationPointTable.UNMOUNTED
    ? sortedInstallationPoints.value
    : []
);
</script>

<style scoped></style>
